<template>
  <div
    class="bh-footer"
    :class="{ 'bh-footer--compact': compact }"
    :lang="$i18n.locale"
  >
    <!-- Tam footer -->
    <div
      v-if="!compact"
      class="bh-footer__inner"
    >
      <div class="bh-footer__brand">
        <router-link
          to="/"
          class="bh-footer__logo"
          :aria-label="info.brand"
        >
          <img
            :src="appLogoImage"
            :alt="info.brand"
          >
        </router-link>
        <span class="bh-footer__badge">
          <strong>18+</strong>
          <span>{{ $t('legal.adultsOnly') }}</span>
        </span>
      </div>

      <nav
        class="bh-footer__col"
        :aria-label="$t('legal.safety')"
      >
        <h6 class="bh-footer__heading">
          {{ $t('legal.safety') }}
        </h6>
        <ul>
          <li
            v-for="link in safetyLinks"
            :key="link.label"
          >
            <router-link :to="link.to">
              {{ link.label }}
            </router-link>
          </li>
        </ul>
      </nav>

      <nav
        class="bh-footer__col"
        :aria-label="$t('legal.legal')"
      >
        <h6 class="bh-footer__heading">
          {{ $t('legal.legal') }}
        </h6>
        <ul>
          <li
            v-for="link in legalLinks"
            :key="link.label"
          >
            <router-link :to="link.to">
              {{ link.label }}
            </router-link>
          </li>
        </ul>
      </nav>

      <div class="bh-footer__col">
        <h6 class="bh-footer__heading">
          {{ $t('legal.contact') }}
        </h6>
        <a
          class="bh-footer__mail"
          :href="`mailto:${info.email}`"
        >
          <feather-icon
            icon="MailIcon"
            size="15"
          />
          <span>{{ info.email }}</span>
        </a>
      </div>
    </div>

    <!-- Kompakt footer (giriş ekranı vb.) -->
    <nav
      v-else
      class="bh-footer__compact-links"
      :aria-label="$t('legal.legal')"
    >
      <!-- Giriş ekranı: üye olmayanlar için, giriş gerektirmeyen ayrı sayfada açılır -->
      <router-link
        v-for="link in compactLinks"
        :key="link.doc"
        :to="publicLegalRoute(link.doc)"
      >
        {{ link.label }}
      </router-link>
    </nav>

  </div>
</template>

<script>
import { $themeConfig } from '@themeConfig'
import { LEGAL_INFO } from '@/libs/legal'

export default {
  name: 'AppFooter',
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      info: LEGAL_INFO,
      appLogoImage: $themeConfig.app.appLogoImage,
    }
  },
  computed: {
    safetyLinks() {
      return [
        { label: this.$t('legal.docs.communityGuidelines'), to: this.docRoute('community-guidelines') },
        { label: this.$t('legal.docs.contentSafety'), to: this.docRoute('content-safety') },
        { label: this.$t('legal.docs.childSafety'), to: this.docRoute('child-safety') },
        { label: this.$t('legal.reportContent'), to: { name: 'legal.report', hash: '#content' } },
        { label: this.$t('legal.reportIllegal'), to: { name: 'legal.report', hash: '#illegal' } },
      ]
    },
    legalLinks() {
      return [
        { label: this.$t('legal.center'), to: { name: 'legal' } },
        { label: this.$t('legal.docs.membershipAgreement'), to: this.docRoute('membership-agreement') },
        { label: this.$t('legal.docs.termsOfService'), to: this.docRoute('terms-of-service') },
        { label: this.$t('legal.docs.privacyPolicy'), to: this.docRoute('privacy-policy') },
      ]
    },
    compactLinks() {
      return [
        { doc: 'membership-agreement', label: this.$t('legal.docs.membershipAgreement') },
        { doc: 'terms-of-service', label: this.$t('legal.docs.termsOfService') },
        { doc: 'privacy-policy', label: this.$t('legal.docs.privacyPolicy') },
        { doc: 'community-guidelines', label: this.$t('legal.docs.communityGuidelines') },
        { doc: 'report', label: this.$t('legal.reportContent') },
      ]
    },
  },
  methods: {
    publicLegalRoute(doc) {
      return { name: 'public-legal', params: { doc } }
    },
    // Giriş ekranındaki 18+ / çerez pencerelerinden gelen bağlantılar da aynı sayfayı açar
    openLegal(doc) {
      this.$router.push(this.publicLegalRoute(doc)).catch(() => {})
    },
    docRoute(slug) {
      return { name: 'legal.document', params: { slug } }
    },
  },
}
</script>

<style lang="scss">
// Dikey layout'taki <footer> sarmalayıcısının varsayılan Vuexy stilini nötrle
.footer.app-footer-wrap {
  padding: 0 !important;
  min-height: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border: 0 !important;
}

.bh-footer {
  --bh-text: rgba(236, 232, 255, 0.72);
  --bh-text-strong: #fff;
  --bh-muted: rgba(236, 232, 255, 0.5);
  --bh-line: rgba(150, 110, 255, 0.2);
  --bh-accent: #f472b6;

  position: relative;
  width: 100%;
  background: linear-gradient(180deg, rgba(20, 12, 48, 0.92), rgba(12, 6, 32, 0.98));
  border-top: 1px solid var(--bh-line);
  color: var(--bh-text);
  font-size: 14px;
  line-height: 1.5;

  a {
    color: var(--bh-text);
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover,
    &:focus-visible {
      color: var(--bh-accent);
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li + li {
    margin-top: 8px;
  }
}

.bh-footer__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px 28px;
  display: grid;
  grid-template-columns: minmax(200px, 1.3fr) repeat(3, minmax(0, 1fr));
  gap: 32px;
}

.bh-footer__logo {
  display: block;
  max-width: 100%;
  margin-bottom: 16px;

  img {
    height: 110px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
  }
}

.bh-footer__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border: 1px solid var(--bh-line);
  border-radius: 999px;
  font-size: 12px;
  color: var(--bh-text);

  strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 24px;
    padding: 0 6px;
    border-radius: 999px;
    background: linear-gradient(135deg, #7c3aed, #ea03b2);
    color: #fff;
    font-weight: 700;
  }
}

.bh-footer__heading {
  margin: 0 0 14px;
  color: var(--bh-text-strong) !important;
  font-size: 15px;
  font-weight: 700;
}

.bh-footer__mail {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  word-break: break-all;
}

.bh-footer__compact-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 18px;
  padding: 14px 16px;
  font-size: 13px;
}

.bh-footer--compact {
  width: auto;
  max-width: 560px;
  margin: 0 auto;
  background: rgba(12, 6, 32, 0.62);
  border: 1px solid var(--bh-line);
  border-radius: 14px;
  backdrop-filter: blur(6px);

}

// Mobilde sabit alt sekme çubuğu footer'ın altını kapatmasın
@media (max-width: 768px) {
  .footer.app-footer-wrap {
    padding-bottom: calc(62px + env(safe-area-inset-bottom, 0px)) !important;
  }
}

@media (max-width: 991px) {
  .bh-footer__inner {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bh-footer__brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 575px) {
  .bh-footer__inner {
    grid-template-columns: 1fr;
    gap: 26px;
    padding: 32px 16px 22px;
  }

  .bh-footer__logo img {
    height: 84px;
  }

}
</style>
